import { Div, Span } from '@codesuma/baseline'
import settings from '../../services/settings'
import calendar from '../../services/calendar'
import { solarYear, paleYear } from '../../helpers/year'
import style from './index.module.css'

// A block of fifty years inside a slider slide, five across and ten down — the
// top of the calendar, and half a century, so two swipes cross one.
//
// `start` is the block's first year (a multiple of 50). Taps travel up as
// 'year-open' for the page to route on.
const SPAN = 50

export const CenturySlide = (start: number) => {
    const base = Div()
    base.addClass(style.slide)

    const { system, yearFormat } = settings.get()
    // Nothing here sets a direction. This slide is a title and a grid, both of
    // them nothing but numbers, and both are pinned left-to-right in either
    // calendar by the stylesheet — see .title and .years.
    const today = calendar.today()
    const thisYear = system === 'gregorian' ? today.gy : today.jy

    // Fifty to a screen leaves each year about four characters wide, so the two
    // eras cannot sit side by side in brackets the way they do in a title. When
    // both are asked for they stack instead, Shahanshahi over Jalaali — see
    // .era and .eraSub. The title below still writes them the usual way.
    const stacked = system !== 'gregorian' && yearFormat === 'both'
    const full = (y: number) => system === 'gregorian' ? String(y) : solarYear(y)

    // A block is one span, so it is written as one. With both eras asked for,
    // the two Shahanshahi years take the dash and the two Jalaali ones follow
    // in a single bracket — ۲۵۸۰ – ۲۶۲۹ (۱۴۰۰ – ۱۴۴۹). Writing it as two whole
    // stamps with a dash between them puts four numbers on the line and makes
    // the reader work out which two are the ends of the range.
    const DASH = ' – '
    const span = (from: number, to: number) => {
        if (system === 'gregorian' || yearFormat !== 'both') return full(from) + DASH + full(to)
        return solarYear(from, 'shahanshahi') + DASH + solarYear(to, 'shahanshahi')
            + ` (${solarYear(from, 'jalaali')}${DASH}${solarYear(to, 'jalaali')})`
    }

    const title = Div()
    title.addClass(style.title)
    title.html(paleYear(span(start, start + SPAN - 1)))
    base.append(title)

    const grid = Div()
    grid.addClass(style.years)
    base.append(grid)

    for (let y = start; y < start + SPAN; y++) {
        const box = Div()
        box.addClass(style.year)
        // Only the year we are in is filled. The rest are numbers on the gray:
        // fifty boxes would be a wall, and there is nothing to tell apart here
        // except which one is now.
        if (y === thisYear) box.addClass(style.thisYear)

        const era = Span(stacked ? solarYear(y, 'shahanshahi') : full(y))
        era.addClass(style.era)
        box.append(era)
        if (stacked) {
            // No brackets: one over the other says the same thing, and there is
            // no room for punctuation at this size.
            const sub = Span(solarYear(y, 'jalaali'))
            sub.addClass(style.eraSub)
            box.append(sub)
        }

        box.attr('role', 'button')
        box.attr('tabindex', '0')
        box.attr('aria-label', `سال ${full(y)}`)
        box.el.onclick = () => base.emit('year-open', y)
        box.el.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                box.el.click()
            }
        })
        grid.append(box)
    }

    return base
}

export const CENTURY_SPAN = SPAN
